import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";
import { DynamicStyles } from "./dynamic-styles";
import { getComparer } from "./sorting-comparer";
import { setupRowspanStyles } from "./setup-rowspan-styles";

@Component({
    tag: "ux-table",
    styleUrl: "table.scss",
    shadow: false
})
export class Table {
    /**
     * Flag which determines if fixed column mechanism should be used
     */
    // eslint-disable-next-line @stencil-community/ban-default-true
    @Prop() readonly useFixedColumn: boolean = true;

    /**
     * Zero-based index of the column that should be fixed on smaller viewports - defaults to the first column
     */
    @Prop() readonly fixedColIndex: number = 0;

    /**
     * Zero-based index of the column that should be used for the title column for the mobile version - defaults to the first column
     */
    @Prop() readonly mobileTitleIndex: number = 0;

    /**
     * Maximum number of pixels that the fixed column is allowed to be wide
     */
    @Prop() readonly maxFixedColWidth: number = 250;

    /**
     * The table should always be at least 100% wide
     */
    @Prop() readonly fullWidth: boolean = false;

    /**
     * The table should sort its static data by itself. Do not use this if the data needs to be sorted by some logic (i.e. via an API)
     */
    @Prop() readonly sortable: boolean = false;

    /**
     * Label for the ux-select that is shown for the sorting of mobile tables
     */
    @Prop() readonly mobileSortLabel: string = "";

    /**
     * The link in the last column will be added into the fixed column on smaller viewports
     */
    @Prop() readonly hasFixedLink: boolean = false;

    /**
     * Setting this to false disables the mobile accordion - showing the scrollable table instead also on smaller screens
     */
    // eslint-disable-next-line @stencil-community/ban-default-true
    @Prop() readonly mobileAccordion: boolean = true;

    /**
     * No-click-selector for ux-accordion in mobile table – use if you use an icon in the mobile-fixed-col
     */
    @Prop() readonly noClickSelector: string = "";

    /**
     * Makes every column the same width, no matter the content
     */
    @Prop() readonly equalColumnWidth: boolean = false;

    /**
     * Makes background colors of the table rows change
     */
    @Prop() readonly striped: boolean = false;

    /**
     * Is true when the table is horizontally scrollable (has a fixed column)
     */
    @State() isFixed = false;

    /**
     * Width of the fixed column
     */
    @State() fixedColWidth = 0;

    /**
     * The left shadow is visible
     */
    @State() leftShadow = false;

    /**
     * The right shadow is visible
     */
    @State() rightShadow = false;

    /**
     * Unique ID used to style this instance via style tag
     */
    @State() uniqueId!: string;

    /**
     * If hasFixedLink is active, we need to add more width to the fixed cell, but only once. Once this flag is set to true, this addition will not be performed, avoiding useless space being added in the table cells.
     */
    @State() fixedLinkSpaceAdded = false;

    /**
     * The fixed column should have a min width set to fit-content to prevent being calculated too small, but if the content of the column exceeds the maximum width of the column specified via prop, then the min-width should be set to auto.
     */
    @State() fixedColumnHasMinWidth = true;

    @Element() element!: HTMLUxTableElement;

    private contentObserver: MutationObserver = new MutationObserver(debounce(() => this.onContentChange(), 100));
    private handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.map((entry) => {
            if (entry.isIntersecting) {
                this.onContentChange();
            }
        });
    };
    private intersectionObserver = new IntersectionObserver(this.handleIntersection);
    private table: HTMLTableElement;
    private innerDiv: HTMLDivElement;
    private mobileTable: HTMLUxMobileTableElement;

    connectedCallback() {
        this.uniqueId = `ux-table-id-${idCounter++}`;
        this.table = this.element.querySelector("table") as HTMLTableElement;
        window.addEventListener("resize", this.onResize);
    }

    componentWillLoad() {
        this.fixedColWidth = this.useFixedColumn ? 250 : 0;
    }

    componentDidLoad() {
        if (this.useFixedColumn) {
            this.addFixedLink();
            this.calculateFixedColWidth();
            this.calculateFixedState();
            this.syncRowHeight();
        }

        this.calculateShadows();
        setupRowspanStyles(this.table);

        this.contentObserver.observe(this.innerDiv, {
            childList: true,
            subtree: true
        });
        this.intersectionObserver?.observe(this.innerDiv);
    }

    disconnectedCallback() {
        window.removeEventListener("resize", this.onResize);
        this.contentObserver.disconnect();
        this.intersectionObserver?.disconnect();
    }

    private onContentChange() {
        if (this.useFixedColumn) {
            this.addFixedLink();
            this.syncRowHeight();
            this.calculateFixedColWidth();
            this.calculateFixedState();
        }
        if (this.mobileAccordion) {
            this.mobileTable.update();
        }
        this.calculateShadows();
    }

    private onResize = throttle(() => {
        if (this.useFixedColumn) {
            this.calculateFixedColWidth();
            this.calculateFixedState();
        }
    }, 200);

    private calculateFixedState() {
        if (this.useFixedColumn) {
            const containerWidth = this.element.getBoundingClientRect().width;
            const tableRect = this.table.getBoundingClientRect();
            const tableWidth = tableRect.width + (this.isFixed ? this.fixedColWidth : 0);
            this.isFixed = containerWidth < tableWidth;
        }
    }

    private calculateFixedColWidth() {
        const { table, fixedColIndex, maxFixedColWidth } = this;
        if (table.clientHeight === 0) {
            return;
        }
        const fixedCells = table.querySelectorAll(
            `td:nth-child(${fixedColIndex + 1})
            ,th:nth-child(${fixedColIndex + 1})`
        );
        const fixedCellsArray = [].slice.call(fixedCells);
        let width = 0;
        // we only calculate the width for the first 50 items to prevent that bigger tables get slow.
        fixedCellsArray.slice(0, 50).forEach((cell: HTMLTableCellElement) => {
            const cellWidth = cell.getBoundingClientRect().width;
            width = cellWidth > width ? cellWidth : width;
        });
        if (!this.fixedLinkSpaceAdded && width !== 0 && this.hasFixedLink) {
            width = width + 15;
            this.fixedLinkSpaceAdded = true;
        }
        this.fixedColWidth = width > maxFixedColWidth || width === 0 ? maxFixedColWidth : width;
        this.fixedColumnHasMinWidth = this.fixedColWidth !== maxFixedColWidth;
    }

    private onScroll = throttle(() => this.calculateShadows(), 10);

    @Watch("isFixed")
    private calculateShadows() {
        const scrollLeft = this.innerDiv.scrollLeft;
        const containerWidth = this.innerDiv.getBoundingClientRect().width;
        const tableWidth = this.table.getBoundingClientRect().width + (this.isFixed ? this.fixedColWidth : 0);
        /**
         * Small buffer for the calculation of the right shadow, fixes the bug that the shadow was not removed for 0.1px of discrepancy
         */
        const rightShadowBuffer = 2;
        this.leftShadow = (this.isFixed || !this.useFixedColumn) && scrollLeft > 0;
        this.rightShadow = scrollLeft + containerWidth + rightShadowBuffer < tableWidth;
    }

    /**
     * The height of multi-line cells might differ from the fixed cell, so we need to sync it to prevent gaps
     */
    @Watch("isFixed")
    private syncRowHeight() {
        const rows = Array.from(this.table.querySelectorAll("tr")) as HTMLTableRowElement[];
        rows.forEach((row: HTMLTableRowElement) => {
            const rowHeight = row.getBoundingClientRect().height;
            const fixedCell: HTMLTableCellElement | null = row.querySelector(
                `th:nth-child(${this.fixedColIndex + 1}),td:nth-child(${this.fixedColIndex + 1})`
            );
            const fixedCellHeight = fixedCell?.getBoundingClientRect().height || 0;
            const height = Math.max(rowHeight, fixedCellHeight);
            row.style.height = `${height}px`;
            if (fixedCell) fixedCell.style.height = `${height}px`;
        });
    }

    @Listen("uxTableSort")
    sortRows(e: CustomEvent) {
        if (!this.sortable) {
            return;
        }
        const numerical = e.detail.numerical;
        const sorter = e.target as HTMLUxTableSortElement;
        const th = sorter.closest("th");
        const titleRow = th?.parentElement;
        const colIndex = th && titleRow ? Array.from(titleRow.children).indexOf(th) : -1;
        const table = th?.closest("table");
        const comparer = getComparer(numerical);

        [].slice
            .call(table?.querySelectorAll(`tr`))
            .filter((tr: HTMLTableRowElement) => !tr.isSameNode(titleRow || null))
            .sort(comparer(colIndex, sorter.sortedDirection === "asc"))
            .forEach((tr: HTMLTableRowElement) => table?.appendChild(tr));
    }

    private addFixedLink(): void {
        if (!this.hasFixedLink) {
            return;
        }

        const tableRowsArray = Array.from(this.table.querySelectorAll(`tr`)) as HTMLElement[];
        const fixedLinkClass = "ux-table__fixed-link";
        tableRowsArray.forEach((tableRow: HTMLElement): void => {
            const lastCellIndex = tableRow.children.length - 1;
            const lastRowCell = tableRow.children[lastCellIndex];
            const link = lastRowCell.querySelector("a");
            if (!link) {
                return;
            }
            const firstRowCell = tableRow.children[this.fixedColIndex];
            const hasFixedLink = firstRowCell.querySelector(`.${fixedLinkClass}`);
            if (!hasFixedLink) {
                const clonedLink = link.cloneNode(true) as HTMLElement;
                clonedLink.className = fixedLinkClass;
                clonedLink.innerHTML = '<ux-icon name="chevron-right" weight="normal"></ux-icon>';
                firstRowCell.insertAdjacentElement("beforeend", clonedLink);
            }
        });
    }

    render() {
        const baseClass = "ux-table";
        const fixedClass = `${baseClass}--is-fixed`;
        const {
            uniqueId,
            isFixed,
            fixedColIndex,
            fixedColWidth,
            maxFixedColWidth,
            leftShadow,
            rightShadow,
            fullWidth,
            fixedColumnHasMinWidth,
            useFixedColumn,
            mobileAccordion,
            equalColumnWidth,
            table
        } = this;

        return (
            <Host class={baseClass}>
                <DynamicStyles
                    uniqueId={uniqueId}
                    fixedColIndex={fixedColIndex}
                    fixedColWidth={fixedColWidth}
                    maxFixedColWidth={maxFixedColWidth}
                    fixedClass={fixedClass}
                    equalColumnWidth={equalColumnWidth}
                    fixedColumnHasMinWidth={fixedColumnHasMinWidth}
                    table={table}
                />
                <div
                    class={{
                        [`${baseClass}--hide-on-mobile`]: mobileAccordion,
                        [fixedClass]: isFixed,
                        [`${baseClass}--has-left-shadow`]: leftShadow,
                        [`${baseClass}--non-fixed-column`]: !useFixedColumn,
                        [`${baseClass}--has-right-shadow`]: rightShadow,
                        [`${baseClass}--full-width`]: fullWidth,
                        [`${baseClass}--same-column-width`]: equalColumnWidth
                    }}
                >
                    <div
                        class={`${baseClass}__inner`}
                        ref={(el) => (this.innerDiv = el as HTMLDivElement)}
                        onScroll={() => this.onScroll()}
                        id={uniqueId}
                        data-scroll-target
                    >
                        <slot />
                    </div>
                </div>
                {mobileAccordion && (
                    <ux-mobile-table
                        sortable={this.sortable}
                        ref={(el) => (this.mobileTable = el as HTMLUxMobileTableElement)}
                        uxTable={this.element}
                        selectLabel={this.mobileSortLabel}
                        noClickSelector={this.noClickSelector}
                    />
                )}
            </Host>
        );
    }
}

/** This variable is incremented by each instance */
let idCounter = 0;
